import { ExprValue } from '@mode-switch/express';
import { SelectResult } from './SelectResult';
export var DomainResult;
(function (DomainResult) {
    function get(domain, result, field) {
        if (field == null) {
            return null;
        }
        const domainQueryResults = getQueryResults(domain, result, field);
        const domainResults = [];
        for (const { result, part } of domainQueryResults) {
            const next = make(result, part);
            if (next != null) {
                domainResults.push(next);
            }
        }
        return domainResults.reduce((prev, next) => {
            if (prev == null) {
                return next;
            }
            if (prev.type !== next.type) {
                const alias = typeof field === 'string' ? field : field.alias;
                throw new Error(`Could not merge domain results for field ${alias}, ` +
                    `types ${prev.type} and ${next.type} are incompatible`);
            }
            switch (prev.type) {
                case "DISCRETE": {
                    return Discrete.merge(prev, next);
                }
                case "CONTINUOUS": {
                    return Continuous.merge(prev, next);
                }
                default: {
                    return null;
                }
            }
        }, null);
    }
    DomainResult.get = get;
    function getQueryResults(domain, result, field) {
        if (field == null) {
            return [];
        }
        const partitions = [];
        if (typeof field === 'string') {
            for (const part of domain.parts) {
                if (part.field.alias === field) {
                    partitions.push(part);
                }
            }
        }
        else {
            for (const part of domain.parts) {
                if (part.field.alias === field.alias) {
                    partitions.push(part);
                }
            }
        }
        const queryResults = [];
        for (const part of partitions) {
            const query = domain.queries.find((query) => query.name === part.query);
            if (query !== undefined) {
                const queryResult = SelectResult.get(result, query);
                if (queryResult !== undefined) {
                    queryResults.push({
                        result: queryResult,
                        part,
                    });
                }
            }
        }
        return queryResults;
    }
    DomainResult.getQueryResults = getQueryResults;
    function make(inline, partition) {
        if (inline.type !== "INLINE") {
            return null;
        }
        switch (partition.role) {
            case "DIMENSION": {
                switch (partition.type) {
                    case "CONTINUOUS": {
                        const item = inline.items[0];
                        const { min, max } = partition;
                        return Continuous.make(item[min.alias], item[max.alias]);
                    }
                    case "DISCRETE": {
                        const alias = partition.values.alias;
                        return Discrete.make(Array.from(new Set(inline.items.map((item) => item[alias]))));
                    }
                }
            }
            case "MEASURE": {
                switch (partition.type) {
                    case "CONTINUOUS": {
                        const items = inline.items;
                        const alias = partition.field.alias;
                        const itemWithValue = items.find((item) => item[alias] != null);
                        let min = itemWithValue === null || itemWithValue === void 0 ? void 0 : itemWithValue[alias];
                        let max = min;
                        if (min != null) {
                            for (const item of items) {
                                const value = item[alias];
                                if (value != null) {
                                    min = ExprValue.lte(value, min) ? value : min;
                                    max = ExprValue.gte(value, max) ? value : max;
                                }
                            }
                        }
                        return Continuous.make(min, max);
                    }
                    case "DISCRETE": {
                        const alias = partition.values.alias;
                        return Discrete.make(Array.from(new Set(inline.items.map((item) => item[alias]))));
                    }
                }
            }
            default: {
                return null;
            }
        }
    }
    let Continuous;
    (function (Continuous) {
        function make(min, max) {
            return {
                type: "CONTINUOUS",
                min,
                max,
            };
        }
        Continuous.make = make;
        function add(domain, value) {
            if (value === null || value === undefined) {
                return domain;
            }
            let { min, max } = domain;
            if (min === null || value < min) {
                min = value;
            }
            if (max === null || value > max) {
                max = value;
            }
            return {
                type: "CONTINUOUS",
                min,
                max,
            };
        }
        Continuous.add = add;
        function merge(domain, other) {
            let { min, max } = domain;
            if (min === null || other.min < min) {
                min = other.min;
            }
            if (max === null || other.max > max) {
                max = other.max;
            }
            return {
                type: "CONTINUOUS",
                min,
                max,
            };
        }
        Continuous.merge = merge;
    })(Continuous = DomainResult.Continuous || (DomainResult.Continuous = {}));
    let Discrete;
    (function (Discrete) {
        function make(values) {
            const keys = new Set(values);
            return {
                type: "DISCRETE",
                keys,
                values,
            };
        }
        Discrete.make = make;
        function add(domain, values) {
            const keys = new Set(domain.keys);
            const nextValues = domain.values.slice();
            for (const value of values) {
                if (!keys.has(value)) {
                    keys.add(value);
                    nextValues.push(value);
                }
            }
            return {
                type: "DISCRETE",
                keys,
                values: nextValues,
            };
        }
        Discrete.add = add;
        function merge(domain, other) {
            return add(domain, other.values);
        }
        Discrete.merge = merge;
    })(Discrete = DomainResult.Discrete || (DomainResult.Discrete = {}));
})(DomainResult || (DomainResult = {}));
